import type { IndustryPage } from "@/lib/data";

export const homeImprovement: IndustryPage = {
  slug: "home-improvement",
  seoTitle:
    "Home Improvement Website Design UK | Fixed £1,000",
  seoDescription:
    "Home improvement website design for kitchen, bathroom, extension and window firms: galleries and quote forms. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for home improvement firms",
  tldr:
    "ConstructionWebDesign.uk offers home improvement website design for kitchen fitters, bathroom fitters, extension builders and window fitters: before-and-after galleries, a page for each specialism and the TrustMark or FENSA details you hold. Each site costs £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. Your website is live in 7 days, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Home improvement websites · UK",
    headline: "Website design for home improvement firms, from kitchens to lofts.",
    highlight: "from kitchens to lofts",
    sub: "Websites for UK home improvement firms with before-and-after project galleries, a page for each specialism from kitchens and bathrooms to extensions, lofts and windows, a quote form that takes photos and a postcode, the TrustMark, FENSA, Checkatrade or FMB badges you hold and on-page local SEO.",
    secondaryCta: { label: "See home-improvement demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/hero/loft_conversion_slider_ratio.jpg",
      alt: "Home-improvement specialist team finishing a loft conversion with rooflights and timber beams",
      headlineTop: "Fill the diary with",
      headlineBottom: "premium home jobs.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/skylofts.jpg",
    },
  ],
  demoSlugs: ["skylofts", "renovia"],
  demosHeading: "Home improvement website demo templates.",
  demosSub:
    "Demo templates for kitchen, bathroom, extension, loft and renovation firms. Open one to see the layout, galleries and pages a home improvement website can include. These are demos, not client sites.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a home improvement firm need a website?",
    intro:
      "A home improvement firm needs a website because homeowners want to see finished kitchens, bathrooms and extensions before they invite anyone round to quote. A home improvement website shows your projects, specialisms, reviews and accreditations in one place, and takes enquiries while you're on a job.",
    points: [
      {
        title: "Show work that looks as good as it is.",
        body: "Homeowners judge the quality of a kitchen fit or bathroom refit from photos. A polished, mobile-first site with sharp photos of your own jobs shows the standard of your work before anyone asks for a quote.",
        icon: "House",
      },
      {
        title: "Give every finished job its own page.",
        body: "Kitchen, bathroom, extension and refurbishment projects can each have their own page with before, during and after photos. A project page named by job type and town gives Google something specific to match when a homeowner searches for that kind of work nearby.",
        icon: "Sparkle",
      },
      {
        title: "Name every specialism you offer.",
        body: "Kitchen fitter, bathroom fitter, extension builder, loft conversion specialist, window and door installer: each specialism you offer gets its own named page that speaks to that job, instead of one long list of services.",
        icon: "Compass",
      },
      {
        title: "Show the trust signals homeowners check.",
        body: "TrustMark registration, FENSA approval for windows and doors, Which? Trusted Trader, Checkatrade and FMB membership sit where visitors look first, linked to the official register or profile. Only the accreditations you actually hold go on the site.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does good home improvement website design include?",
    intro:
      "A good home improvement website shows finished projects first, gives each specialism its own page and makes it easy to ask for a quote or a home visit. Every home improvement website from ConstructionWebDesign.uk is planned around the parts below.",
    items: [
      {
        title: "Before-and-after project galleries.",
        body: "Each finished project gets its own page with a slider showing the original room next to your finished work, plus the scope, the materials and how long the job took. Kitchens, bathrooms, extensions and loft conversions can each have their own gallery.",
      },
      {
        title: "A page for each specialism.",
        body: "Kitchen fitting, bathroom fitting, extensions, loft conversions, windows and doors, full-house refurbishments, garden rooms and period restorations can each have their own page. Each page targets its own search, so someone looking for a bathroom fitter lands on your bathroom page, not a general list.",
      },
      {
        title: "Home visit and design consultation requests.",
        body: "A short form lets a homeowner ask for a home visit, survey or design consultation and say which room or project it's for. You know what the job is before you ring back to book the visit.",
      },
      {
        title: "Guide prices and a quote form.",
        body: "Publish your own guide prices for common jobs, such as a kitchen fit, a bathroom refit or a single-storey rear extension, if you want to. The quote form asks for a postcode, photos of the room and a budget band, so you have what you need to price the job before you ring back.",
      },
      {
        title: "TrustMark, FENSA and trade body badges near the top.",
        body: "TrustMark is the government-endorsed quality scheme for work in and around the home, and FENSA is the competent person scheme for replacement windows and doors in England and Wales. If you're TrustMark registered, FENSA approved, a Which? Trusted Trader, on Checkatrade or an FMB member, the badge sits in the header, linked to the official register or profile. Anything you don't hold stays off the site.",
      },
      {
        title: "Local SEO built in from day one.",
        body: "Every home improvement website includes on-page local SEO: schema markup, pages for the towns you cover, town and specialism keywords in page titles and a link to your Google Business Profile.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a home improvement website cost in the UK?",
      a: "A home improvement website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with a logo and branding. For comparison, typical UK ranges are about £4,000–£5,000 for a freelancer billing £50–£120 an hour, £5,000–£25,000 for an agency, or about £22 a month for Wix or Squarespace. You own the website.",
    },
    {
      q: "How long does it take to build a home improvement website?",
      a: "A home improvement website from ConstructionWebDesign.uk goes live 7 days after the discovery call. The five steps are a discovery call, strategy and sitemap, design, build, then launch and support. On the discovery call you explain the specialisms you want more work in and the areas you cover. For comparison, a typical UK agency project takes 8–12 weeks.",
    },
    {
      q: "What pages should a home improvement website have?",
      a: "A home improvement website should have a homepage with a click-to-call button, a page for each specialism (kitchens, bathrooms, extensions, loft conversions, windows and doors), before-and-after project galleries, pages for the areas you cover, your reviews and a quote form that takes photos. TrustMark, FENSA or FMB badges belong near the top if you hold them. ConstructionWebDesign.uk agrees the sitemap with you before design starts.",
    },
    {
      q: "Do home improvement businesses need a website?",
      a: "Yes, home improvement businesses need a website, because a homeowner planning a new kitchen, bathroom or extension wants to see finished work before inviting anyone to quote. A home improvement website shows your projects, specialisms, reviews and accreditations in one place. Checkatrade and directory profiles help, but they list you next to other firms. Your own website shows only your business, takes enquiries while you're on a job and belongs to you.",
    },
    {
      q: "How do home improvement companies get found on Google?",
      a: "Home improvement companies get found on Google through a website with a page for each specialism and area, on-page local SEO and a complete Google Business Profile with regular reviews. Every home improvement website from ConstructionWebDesign.uk includes on-page local SEO: schema markup, area pages, town and specialism keywords in page titles and a link to your Google Business Profile. Nobody can honestly guarantee a ranking, but these are the basics a home improvement firm needs.",
    },
    {
      q: "Do you build websites for kitchen fitters and bathroom fitters?",
      a: "Yes, ConstructionWebDesign.uk builds websites for kitchen fitters and bathroom fitters for a fixed £1,000, one-off. A kitchen or bathroom fitter website leads with before-and-after photos of finished rooms, has a page for each service, such as full kitchen installs, worktop fitting, bathroom refits or wet rooms, and a quote form that takes photos of the existing room.",
    },
    {
      q: "Should a window installer show FENSA on their website?",
      a: "Yes, a window installer who is FENSA approved should show it on the website, because FENSA approval lets the installer self-certify that replacement windows and doors meet Building Regulations in England and Wales. Homeowners receive a FENSA certificate, and buyers' solicitors usually ask for it when the house is sold. Home improvement website design from ConstructionWebDesign.uk puts FENSA, TrustMark and other badges you hold near the top, linked to the official register.",
    },
    {
      q: "Can I build my own home improvement website on Wix or Squarespace?",
      a: "Yes, a home improvement firm can build a website on Wix, Squarespace or WordPress, and Wix or Squarespace typically cost about £22 a month. The trade-off is time: you set up the galleries, write a page for each specialism and add local SEO yourself, and a Wix or Squarespace site goes offline if you stop paying. ConstructionWebDesign.uk builds home improvement websites at a fixed £1,000, and you own the result.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/how-to-get-more-construction-leads",
      label: "How to get more construction leads without paying",
    },
    {
      href: "/blog/local-seo-for-builders",
      label: "Local SEO for builders: rank for \"builder near me\"",
    },
    {
      href: "/blog/how-to-price-building-work",
      label: "How to price building work without losing money",
    },
  ],
  finalCta: {
    headline: "Talk to us about your home improvement website.",
    highlight: "home improvement website",
    sub: "Book a free 30-minute call or email hello@constructionwebdesign.uk. Fixed £1,000, one-off, and your website is live in 7 days.",
  },
};
